import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplet, Heart, Star, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui';

interface OilPatch {
  id: string;
  x: number;
  y: number;
  size: number;
  cleaned: number; // 0-100
}

interface OilCleaningGameProps {
  onComplete: (score: number, stars: number) => void;
  onBack: () => void;
  speciesId: string;
  otterName: string;
}

const STRESS_MAX = 100;
const CLEAN_TARGET = 95;
const TOOLS = ['soap', 'sponge', 'rinse'] as const;
type Tool = typeof TOOLS[number];

export function OilCleaningGame({ onComplete, onBack, speciesId, otterName }: OilCleaningGameProps) {
  const [patches, setPatches] = useState<OilPatch[]>(() => generatePatches());
  const [stress, setStress] = useState(0);
  const [selectedTool, setSelectedTool] = useState<Tool>('soap');
  const [cleanPercentage, setCleanPercentage] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [tapsInArea, setTapsInArea] = useState(0);
  const lastTapRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const gameAreaRef = useRef<HTMLDivElement>(null);

  function generatePatches(): OilPatch[] {
    const count = 8 + Math.floor(Math.random() * 5);
    return Array.from({ length: count }, (_, i) => ({
      id: `patch-${i}`,
      x: 15 + Math.random() * 70,
      y: 20 + Math.random() * 60,
      size: 30 + Math.random() * 40,
      cleaned: 0,
    }));
  }

  // Update overall clean percentage
  useEffect(() => {
    const totalCleaned = patches.reduce((sum, p) => sum + p.cleaned, 0);
    const maxClean = patches.length * 100;
    const percentage = (totalCleaned / maxClean) * 100;
    setCleanPercentage(percentage);

    if (percentage >= CLEAN_TARGET && !isComplete) {
      setIsComplete(true);
      const stars = stress < 30 ? 3 : stress < 60 ? 2 : 1;
      setTimeout(() => onComplete(Math.floor(percentage), stars), 1500);
    }
  }, [patches, isComplete, stress, onComplete]);

  // Decay stress over time
  useEffect(() => {
    const interval = setInterval(() => {
      setStress(prev => Math.max(0, prev - 0.5));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const handleClean = useCallback((clientX: number, clientY: number) => {
    if (isComplete) return;

    const gameArea = gameAreaRef.current;
    if (!gameArea) return;

    const rect = gameArea.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    // Check for rapid tapping (causes stress)
    const now = Date.now();
    if (lastTapRef.current) {
      const timeDiff = now - lastTapRef.current.time;
      const posDiff = Math.hypot(x - lastTapRef.current.x, y - lastTapRef.current.y);

      if (timeDiff < 200 && posDiff < 10) {
        setTapsInArea(prev => prev + 1);
        if (tapsInArea > 3) {
          setStress(prev => Math.min(STRESS_MAX, prev + 5));
        }
      } else {
        setTapsInArea(0);
      }
    }
    lastTapRef.current = { x, y, time: now };

    // Clean patches near the tap
    const cleanRadius = selectedTool === 'sponge' ? 20 : selectedTool === 'soap' ? 15 : 25;
    const cleanPower = selectedTool === 'soap' ? 15 : selectedTool === 'sponge' ? 25 : 10;

    setPatches(prev => prev.map(patch => {
      const distance = Math.hypot(patch.x - x, patch.y - y);
      if (distance < cleanRadius + patch.size / 2) {
        const effectiveness = 1 - (distance / (cleanRadius + patch.size / 2));
        const cleanAmount = cleanPower * effectiveness;
        return {
          ...patch,
          cleaned: Math.min(100, patch.cleaned + cleanAmount),
        };
      }
      return patch;
    }));

    // Small stress increase for any action
    setStress(prev => Math.min(STRESS_MAX, prev + 0.5));
  }, [isComplete, selectedTool, tapsInArea]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleClean(touch.clientX, touch.clientY);
  }, [handleClean]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleClean(touch.clientX, touch.clientY);
  }, [handleClean]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleClean(e.clientX, e.clientY);
  }, [handleClean]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (e.buttons === 1) {
      handleClean(e.clientX, e.clientY);
    }
  }, [handleClean]);

  const stressColor = stress < 30 ? 'text-green-500' : stress < 60 ? 'text-yellow-500' : 'text-red-500';
  const stressBgColor = stress < 30 ? 'bg-green-500' : stress < 60 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="fixed inset-0 bg-otter-900 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onBack} className="text-white/60 text-sm">
            ← Back
          </button>
          <h2 className="text-white font-bold">Oil Cleaning</h2>
          <div className="w-12" />
        </div>

        {/* Progress bars */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Droplet className="w-4 h-4 text-blue-400" />
            <div className="flex-1 h-3 bg-black/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-blue-500"
                initial={{ width: 0 }}
                animate={{ width: `${cleanPercentage}%` }}
              />
            </div>
            <span className="text-white text-xs w-12 text-right">
              {Math.floor(cleanPercentage)}%
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Heart className={`w-4 h-4 ${stressColor}`} />
            <div className="flex-1 h-3 bg-black/30 rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${stressBgColor}`}
                animate={{ width: `${stress}%` }}
              />
            </div>
            <span className="text-white text-xs w-12 text-right">
              Stress
            </span>
          </div>
        </div>
      </div>

      {/* Game area */}
      <div
        ref={gameAreaRef}
        className="flex-1 relative overflow-hidden touch-none select-none"
        style={{ backgroundColor: '#3498db' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      >
        {/* Otter silhouette */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
          <div className="text-9xl">🦦</div>
        </div>

        {/* Oil patches */}
        {patches.map(patch => (
          <motion.div
            key={patch.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${patch.x}%`,
              top: `${patch.y}%`,
              width: patch.size,
              height: patch.size,
              transform: 'translate(-50%, -50%)',
              background: `radial-gradient(circle, rgba(30,30,30,${1 - patch.cleaned / 100}) 0%, rgba(60,40,20,${0.8 - patch.cleaned / 100 * 0.8}) 100%)`,
            }}
            animate={{
              scale: 1 - patch.cleaned / 200,
              opacity: 1 - patch.cleaned / 100,
            }}
          />
        ))}

        {/* Cleaning effect cursor */}
        <AnimatePresence>
          {!isComplete && (
            <motion.div
              className="absolute pointer-events-none text-center text-white/60 text-sm"
              style={{ left: '50%', bottom: 20, transform: 'translateX(-50%)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Tap and drag to clean! Be gentle.
            </motion.div>
          )}
        </AnimatePresence>

        {/* Completion overlay */}
        <AnimatePresence>
          {isComplete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-black/60 flex items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-green-600 rounded-2xl p-6 text-center"
              >
                <CheckCircle className="w-16 h-16 text-white mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-white mb-2">
                  All Clean!
                </h3>
                <p className="text-white/80">
                  {otterName} is oil-free and feeling better!
                </p>
                <div className="flex justify-center gap-1 mt-3">
                  {[1, 2, 3].map(i => (
                    <Star
                      key={i}
                      className={`w-8 h-8 ${
                        (stress < 30 && i <= 3) || (stress < 60 && i <= 2) || i === 1
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tool selection */}
      <div className="p-4 bg-black/30">
        <div className="flex gap-2 justify-center">
          {TOOLS.map(tool => (
            <Button
              key={tool}
              variant={selectedTool === tool ? 'primary' : 'secondary'}
              onClick={() => setSelectedTool(tool)}
              className="flex-1 capitalize"
            >
              {tool === 'soap' && '🧴'}
              {tool === 'sponge' && '🧽'}
              {tool === 'rinse' && '🚿'}
              <span className="ml-2">{tool}</span>
            </Button>
          ))}
        </div>
        <p className="text-white/50 text-xs text-center mt-2">
          {selectedTool === 'soap' && 'Soap: Medium area, good cleaning'}
          {selectedTool === 'sponge' && 'Sponge: Small area, powerful cleaning'}
          {selectedTool === 'rinse' && 'Rinse: Large area, light cleaning'}
        </p>
      </div>
    </div>
  );
}
